import { nayttonimiPieni } from "./nayttonimi";
import { TunnuksenKieli } from "./KieliValinta";
import { kantaTeksti, KIELEN_NIMI, t, virheTeksti, type KaannosAvain } from "./kieli";
import type { Kieli } from "./shared/types";
import { Fragment, useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { naytaPvm, naytaPvmKello } from "./pvm";
import { Salaisuus } from "./Salaisuus";
import { Tilasana } from "./Tila";
import { TyomaaValinta } from "./TyomaaValinta";
import {
  type VientilokiRivi,
  api, ApiError, varaaKapasiteettia,
  type Kayttaja, type KayttajaRivi, type Resurssi, type Rooli, type Moduuli,
  type Kohde, type Kenttatili,
} from "./api";

import { Ohje } from "./Ohje";
import { InfoNappi, OikeusEditori, PakettiJoukolle } from "./OikeusEditori";
/* ═══════════════════════════════════════════════════════════
   Käyttäjät ja roolit.

   Vain pääkäyttäjä. Roolit ovat kiinteät, koska oikeudet
   tarkistetaan koodissa tunnuksen perusteella — kantaan lisätty
   neljäs rooli ei tekisi mitään. Roolien jakaminen sen sijaan
   kuuluu käyttöliittymään.
   ═══════════════════════════════════════════════════════════ */

export default function Kayttajat({ kayttaja }: { kayttaja: Kayttaja }) {
  const [rivit, setRivit] = useState<KayttajaRivi[]>([]);
  /* T530: joukkoanto — valitut selaintunnukset «Anna paketti» -toimintoa varten. */
  const [joukko, setJoukko] = useState<number[]>([]);
  /** pääChat Tonin välittämänä 12.10.2026: passiiviset piiloon oletuksena, valintaruutu näyttää ne. */
  const [naytaPassiiviset, setNaytaPassiiviset] = useState(false);
  const [joukkoIlmoitus, setJoukkoIlmoitus] = useState<string | null>(null);
  const [roolit, setRoolit] = useState<Rooli[]>([]);
  const [henkilot, setHenkilot] = useState<Resurssi[]>([]);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [oikeusluettelo, setOikeusluettelo] = useState<{ tunnus: string; nimi: string; kuvaus: string | null; kohderajattava: boolean;
                                                          moduuli?: string | null; moduuli_kaytossa?: boolean }[]>([]);
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lisataan, setLisataan] = useState(false);
  const [avattu, setAvattu] = useState<number | null>(null);
  // A8: kertanäyttöinen salaisuus näytetään modaalissa siinä kohtaa missä teko tapahtui,
  // ei ylälaidan paneelissa. `kayttajaId` sitoo kutsun riviin ja mahdollistaa lähetyksen (B2).
  const [ilmoitus, setIlmoitus] = useState<
    { nimi: string; arvo: string; laji: "kutsu" | "salasana"; kayttajaId?: number; osoite?: string | null; syy?: string | null } | null>(null);

  const lataa = useCallback(async () => {
    setLadataan(true);
    setVirhe(null);
    try {
      const [k, r, h, ko, ol] = await Promise.all([
        api.kayttajat(), api.roolit(), api.resurssit(), api.kohteet(), api.oikeusluettelo(),
      ]);
      setRivit(k); setRoolit(r); setHenkilot(h.filter((x) => !x.nimeamaton)); setKohteet(ko);
      setOikeusluettelo(ol);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.haku_virhe"));
    } finally { setLadataan(false); }
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  if (virhe) {
    return (
      <div className="tyhja">
        <p>{virhe}</p>
        <button className="nappi nappi--paa" onClick={() => void lataa()}>{t("hen.yrita_uudelleen")}</button>
      </div>
    );
  }

  return (
    <>
      <header className="head">
        <div className="eyebrow">{t("valikko.tunnukset")}</div>
        <div className="head__rivi">
          <h1>{t("kay.otsikko")}</h1>
          {!lisataan && (
            <button className="nappi nappi--paa" onClick={() => setLisataan(true)} data-uusi-tunnus="">
              {t("kay.uusi_tunnus")}
            </button>
          )}
        </div>
      </header>

      {/* Ohje ylös (Toni 23.9.2026): kuka saa tehdä mitä, ja mitä luvut tarkoittavat. */}
      <Ohje
        kuka={<>{t("kay.ohje.kuka")}</>}
        lukujenOtsikko={t("kay.ohje.hyva_tietaa")}
        luvut={<>{t("kay.ohje.luvut")}</>} />

      {ilmoitus && (
        <Salaisuus
          otsikko={ilmoitus.laji === "kutsu" ? t("kay.kutsulinkki") : t("kay.kertasalasana")}
          kohde={ilmoitus.nimi}
          rivit={[{ selite: ilmoitus.laji === "kutsu" ? t("kay.linkki") : t("kirjautuminen.salasana"), arvo: ilmoitus.arvo }]}
          vihje={ilmoitus.laji === "kutsu"
            ? t("kay.vihje_kutsu")
            : t("kay.vihje_salasana")}
          lahetys={ilmoitus.laji === "kutsu" && ilmoitus.kayttajaId ? {
            osoite: ilmoitus.osoite ?? null, syy: ilmoitus.syy ?? null,
            laheta: async () => {
              const tk = await api.luoKutsu(ilmoitus.kayttajaId!, true);
              setIlmoitus((v) => (v ? { ...v, arvo: tk.kutsu } : v));
              return tk.lahetys.lahetetty
                ? { ok: true, viesti: t("kay.linkki_sama") }
                : { ok: false, viesti: t("kay.ei_lahetetty", { syy: tk.lahetys.syy ?? t("kay.tuntematon_syy") }) };
            },
          } : undefined}
          sulje={() => setIlmoitus(null)} />
      )}

      {lisataan && (
        <UusiTunnus
          henkilot={henkilot} roolit={roolit} kohteet={kohteet} rivit={rivit}
          peruuta={() => setLisataan(false)}
          valmis={async (nimi, arvo, lisa) => {
            setLisataan(false);
            // Toni 11.9.: «nyt kun saa vielä meiliin» — luontimodaali avattiin ilman
            // tunnuksen id:tä ja osoitetta, joten B2:n lähetysnappi jäi pois juuri
            // siitä paikasta jossa osoite on juuri kirjoitettu.
            setIlmoitus({ nimi, arvo, laji: "kutsu", ...lisa });
            await lataa();
          }}
        />
      )}

      {ladataan && <div className="lataa lataa--ohut">{t("yleinen.ladataan")}</div>}

      {/* Rivi 84 (11.9.2026): kolme eri asiaa, jotka olivat yhdessä listassa —
          HENKILÖ (tekee työtä, henkilörekisterissä) · TUNNUS (kirjautuu selaimella,
          osoite on reitti jota pitkin kutsu kulkee) · KENTTÄTILI (kirjautuu koodilla
          ja PIN:llä, ei osoitetta). Tämä lista on TUNNUKSET; kenttätilit ovat
          omassa osiossaan alempana, ja henkilö näkyy tunnuksen liitoksena. */}
      <p className="hint" style={{ margin: "0 0 8px" }}>
        {t("kay.lista.a")} <strong>{t("kay.lista.selaintunnukset")}</strong> {t("kay.lista.b")}
        <strong> {t("kay.lista.kenttatilit")}</strong> {t("kay.lista.c")} <strong>{t("kay.lista.henkilo")}</strong> {t("kay.lista.d")}
      </p>
      <NaytaPassiiviset arvo={naytaPassiiviset} muuta={setNaytaPassiiviset}
                        n={rivit.filter((r) => !r.kenttatunnus && !r.aktiivinen).length} merkinta="tunnukset" />
      <div className="oik__joukkorivi">
        <PakettiJoukolle valitut={joukko} kohteet={kohteet} oikeusluettelo={oikeusluettelo} ilmoita={setJoukkoIlmoitus}
                         valmis={async () => { setJoukko([]); await lataa(); }} />
        {joukkoIlmoitus && <p className="hint" role="status" data-joukko-ilmoitus="">{joukkoIlmoitus}</p>}
      </div>
      {/* T677 A / T532 (Toni 27.10.2026 klo 10.10): taulukko vierii omassa ikkunassaan — vaakapalkki näkyvissä eikä taulukko
          venytä puhelimen sivua. */}
      <section className="panel taulu-ikkuna" data-taulu-ikkuna="tunnukset">
        <table className="tbl tbl--kayttajat">
          <thead>
            <tr>
              <th className="th--nimi">{t("iv.nimi")}</th>
              <th className="th--vasen">{t("kirjautuminen.sahkoposti")}</th>
              <th className="th--vasen">{t("kay.roolit")}</th>
              <th className="th--vasen">{t("kay.nakyvyys")}</th>
              {/* Toni 14.9.2026 / 18.9.2026: «Istuntoja» luettiin kertymäksi — «47» näytti
                  siltä että vahti on kirjautunut 47 kertaa, kun luku on voimassa olevia
                  istuntoja juuri nyt (T335). Ensisijainen sarake on nyt se mitä lukija
                  luulikin lukevansa: milloin tunnus kävi viimeksi. Auki olevien luku jää
                  omalla nimellään sen viereen. */}
              <th>{t("kay.th.viimeksi")}</th>
              <th>{t("kay.th.auki_nyt")}</th>
              <th>{t("pu.tila")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rivit.filter((r) => !r.kenttatunnus && (r.aktiivinen || naytaPassiiviset)).map((r) => (
              <KayttajaRiviKomponentti
                key={r.id} rivi={r} roolit={roolit} kohteet={kohteet} oikeusluettelo={oikeusluettelo}
                henkilot={henkilot} omaId={kayttaja.id}
                tunnukset={rivit.filter((x) => x.aktiivinen).map((x) => ({ id: x.id, nimi: x.nimi }))}
                valittu={joukko.includes(r.id)} valitse={() => setJoukko(joukko.includes(r.id) ? joukko.filter((x) => x !== r.id) : [...joukko, r.id])}
                auki={avattu === r.id}
                avaa={() => setAvattu(avattu === r.id ? null : r.id)}
                paivita={lataa}
                nayta={(nimi, arvo, laji, lisa) => setIlmoitus({ nimi, arvo, laji, ...(lisa ?? {}) })}
              />
            ))}
          </tbody>
        </table>
      </section>

      {/* Toni 13.9.2026: selaintunnukset ylös, kenttätilit alle — kenttätilejä on kymmenkertaisesti enemmän,
          ja harvempi lista kuuluu ylös. */}
      <KenttatilitLuonti onValmis={lataa} kohteet={kohteet} oikeusluettelo={oikeusluettelo} />

      <Moduulit />

      <Vientiloki />

    </>
  );
}

/* ── vientiloki (148) ───────────────────────────────────────
   Toni 20.9.2026. Aineisto joka lähtee talosta on se jota ei voi
   kutsua takaisin, eikä siitä jäänyt aiemmin mitään: 13 Excel-reittiä,
   nolla kirjausta. Tämä näkymä on lokin lukupää.

   ⚠ Loki alkaa tyhjänä eikä sitä täytetä takautuvasti. Tyhjä lista
   ennen ensimmäistä vientiä EI tarkoita ettei mitään olisi viety — se
   tarkoittaa ettei sitä tiedetä. Siksi näkymä sanoo mistä hetkestä
   loki kattaa, eikä jätä lukijaa päättelemään sitä rivien puutteesta. */
function Vientiloki() {
  const [data, setData] = useState<{ rivit: VientilokiRivi[]; alkoi: string | null } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);

  useEffect(() => {
    api.vientiloki(50)
      .then(setData)
      .catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.vientiloki_virhe")));
  }, []);

  return (
    <section className="panel scroll">
      <div className="panel__head">
        <h2>{t("kay.vientiloki")}</h2>
        <span className="hint">
          {data?.alkoi
            ? t("kay.vientiloki_kattaa", { pvm: naytaPvm(data.alkoi) ?? "" })
            : t("kay.vientiloki_tyhja_hint")}
        </span>
      </div>

      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      {!data && !virhe && <p className="dim">{t("yleinen.ladataan")}</p>}

      {data && data.rivit.length === 0 && (
        <p className="dim">
          {t("kay.vientiloki_tyhja")}
        </p>
      )}

      {data && data.rivit.length > 0 && (
        <table className="tbl">
          <thead>
            <tr>
              <th className="th--vasen">{t("kay.th.aika")}</th>
              <th className="th--vasen">{t("kay.kuka")}</th>
              <th className="th--vasen">{t("kay.th.vienti")}</th>
              <th className="th--vasen">{t("kuittaus.kohde")}</th>
              <th>{t("kay.th.riveja")}</th>
            </tr>
          </thead>
          <tbody>
            {data.rivit.map((r) => (
              <tr key={r.id}>
                <td>{naytaPvmKello(r.hetki)}</td>
                <td>{r.kayttaja_nimi}</td>
                <td title={r.tiedosto ?? ""}>{r.vienti}</td>
                <td>{r.kohde_tunnus ?? <span className="dim">—</span>}</td>
                {/* Tyhjä ja nolla ovat eri asia: «ei laskettu» ei ole
                    «nolla riviä», ja viiva sanoo kumpi. */}
                <td className="mono">
                  {r.rivit === null ? <span className="dim">—</span> : r.rivit}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/* ── yksi käyttäjä ──────────────────────────────────────── */

function KayttajaRiviKomponentti({
  oikeusluettelo,
  rivi, roolit, kohteet, henkilot, omaId, auki, avaa, paivita, nayta, tunnukset, valittu, valitse,
}: {
  rivi: KayttajaRivi; roolit: Rooli[]; kohteet: Kohde[]; henkilot: Resurssi[]; omaId: number;
  tunnukset: { id: number; nimi: string }[]; valittu: boolean; valitse: () => void;
  oikeusluettelo: { tunnus: string; nimi: string; kuvaus: string | null; kohderajattava: boolean; moduuli?: string | null; moduuli_kaytossa?: boolean }[];
  auki: boolean; avaa: () => void; paivita: () => Promise<void>;
  nayta: (nimi: string, arvo: string, laji: "kutsu" | "salasana", lisa?: { kayttajaId: number; osoite: string | null; syy: string | null }) => void;
}) {
  const [valitut, setValitut] = useState<string[]>(rivi.roolit);
  const [valitutKohteet, setValitutKohteet] = useState<number[]>(rivi.kohteet);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  const ARKALUONTEISET = "arkaluonteiset_henkilotiedot";
  const arkaluonteiset = rivi.oikeudet.includes(ARKALUONTEISET);

  /* ⛔ Palautus vain kun TALLENNETTU arvo muuttuu (24.9.2026). Riippuvuus oli
     koko `rivi`: jokainen heti tallentuva toiminto samalla rivillä (oikeus,
     henkilöliitos, kutsulinkki, passivointi) lataa listan uudelleen, ja
     tallentamattomat roolit ja näkyvyys nollautuivat hiljaa — «Tallentamatta»
     katosi niiden mukana. */
  const tallennetutRoolit = rivi.roolit.join(",");
  const tallennetutKohteet = rivi.kohteet.join(",");
  useEffect(() => {
    setValitut(rivi.roolit);
    setValitutKohteet(rivi.kohteet);
  }, [tallennetutRoolit, tallennetutKohteet]);

  /**
   * Oikeus tallentuu heti, ei "Tallenna"-napin takaa.
   *
   * Roolit ovat joukko jota muokataan ja tallennetaan kerralla; oikeus
   * on yksittäinen päätös jolla on myöntäjä ja aikaleima. Jos se
   * odottaisi samaa nappia, myöntäminen voisi tapahtua vahingossa
   * roolimuutoksen sivutuotteena.
   */
  /* Valikon oma tila: ohjattu select tarvitsee sen, ja se on
   * alustettava rivin ID:sta eika nimesta. */
  const [henkiloValinta, setHenkiloValinta] =
    useState<string>(rivi.henkilo_id === null ? "" : String(rivi.henkilo_id));
  useEffect(() => {
    setHenkiloValinta(rivi.henkilo_id === null ? "" : String(rivi.henkilo_id));
  }, [rivi.henkilo_id]);

  async function liitaHenkilo(arvo: string) {
    setTallennetaan(true); setIlmoitus(null);
    try {
      await api.muokkaaKayttajaa(rivi.id, { henkilo_id: arvo === "" ? 0 : Number(arvo) });
      setIlmoitus(arvo === "" ? t("kay.liitos_poistettu") : t("kay.henkilo_liitetty"));
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.liitos_virhe"));
    } finally { setTallennetaan(false); }
  }

  /* 163: erilliset oikeudet yleisenä listana. Ennen tätä vain arkaluonteisten
     tietojen oikeutta voi myöntää käyttöliittymästä — punakynän hyväksyntä ja
     viestien lähettäminen vaativat kantakäsittelyn. Kohderajattava oikeus
     myönnetään joko koko yritykselle tai valituille kohteille. */

  const [arkKuittaus, setArkKuittaus] = useState<string | null>(null);
  /* T530 (12.10.2026): laajennettu rivi on taulukon sisällä, ja leveä taulukko venytti editorin vierityspaneelin näkyvän alueen yli
     (kolmas pakettikortti jäi piiloon 1280 px:ssä). Editorin leveys = vierityspaneelin näkyvä leveys, ja se pysyy vasemmassa reunassa. */
  const laajennusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // T677 A (27.10.2026): paneeli on nyt .taulu-ikkuna. Pelkkä ".scroll" ei löytänyt sitä, leveyttä ei asetettu, ja editori venyi
    // taulukon levyiseksi (demo 1024 px: 1 188 px, paneeli 744 px; kävely «tunnuseditori näkyvissä» ✗).
    const el = laajennusRef.current; const paneeli = el?.closest(".taulu-ikkuna, .scroll") as HTMLElement | null;
    if (!el || !paneeli) return;
    // 12.10.2026 (protot, Tonin kuva): leveys miinus solun vaakatäyte, ja solu kattaa kaikki 8 saraketta (oli 7). Ennen tätä
    // editori + viimeinen sarake teki taulukosta aina paneelia leveämmän: Muokkaa-napit jäivät vierityksen taakse kaikilla
    // leveyksillä 1024–1920 (0/21 näkyvissä) ja oikealle vieritettäessä editorin vasen reuna leikkautui.
    const td = el.closest("td");
    const tayte = () => { if (!td) return 0; const c = getComputedStyle(td); return parseFloat(c.paddingLeft) + parseFloat(c.paddingRight); };
    const aseta = () => { el.style.width = Math.max(240, paneeli.clientWidth - tayte()) + "px"; };
    aseta(); const ro = new ResizeObserver(aseta); ro.observe(paneeli);
    return () => ro.disconnect();
  }, [auki]);
  async function vaihdaArkaluonteiset() {
    setTallennetaan(true);
    setIlmoitus(null); setArkKuittaus(null);
    try {
      if (arkaluonteiset) {
        await api.poistaOikeus(rivi.id, ARKALUONTEISET);
        setIlmoitus(t("kay.oikeus_poistettu")); setArkKuittaus(t("kay.oikeus_poistettu"));
      } else {
        await api.myonnaOikeus(rivi.id, ARKALUONTEISET);
        setIlmoitus(t("kay.oikeus_myonnetty")); setArkKuittaus(t("kay.oikeus_myonnetty"));
      }
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.muutos_virhe"));
    } finally { setTallennetaan(false); }
  }

  const oma = rivi.id === omaId;

  /* «Tallenna muutokset» (Toni 24.9.2026). Nappi luki «Tallenna roolit» ja
     tallensi vain roolit, vaikka samassa osiossa näkyvyys odotti omaa nappiaan
     keskellä sivua — näkyvyyden muutos katosi hiljaa, jos painoi alareunan
     isoa nappia. Nyt yksi painallus tallentaa kaiken joka odottaa napin takana
     (roolit ja näkyvyys), ja ilmoitus kertoo osa kerrallaan mikä tallentui.
     Kaksi kutsua eikä yksi transaktio: jos jälkimmäinen kaatuu, ilmoitus
     sanoo että ensimmäinen ehti tallentua. Oikeudet eivät ole tässä — ne
     tallentuvat valittaessa (perustelu alla). */
  const samat = <T,>(a: T[], b: T[]) => a.length === b.length && a.every((x) => b.includes(x));
  const roolitMuuttui = !samat(valitut, rivi.roolit);
  const kohteetMuuttui = !samat(valitutKohteet, rivi.kohteet);
  const tallentamatta = [roolitMuuttui && "roolit", kohteetMuuttui && "näkyvyys"].filter(Boolean) as string[];

  async function tallenna() {
    if (tallentamatta.length === 0) { setIlmoitus(t("kay.ei_tallentamattomia")); return; }
    if (roolitMuuttui && valitut.length === 0) { setIlmoitus(t("kay.valitse_rooli")); return; }
    setTallennetaan(true);
    setIlmoitus(null);
    const tehty: string[] = [];
    try {
      if (roolitMuuttui) {
        await api.muokkaaKayttajaa(rivi.id, { roolit: valitut });
        tehty.push(t("kay.osa.roolit"));
      }
      if (kohteetMuuttui) {
        await api.asetaKayttajanKohteet(rivi.id, valitutKohteet);
        tehty.push(valitutKohteet.length === 0
          ? t("kay.osa.nakyvyys_kaikki")
          : t("kay.osa.nakyvyys_n", { n: valitutKohteet.length }));
      }
      setIlmoitus(t("kay.tallennettu", { osat: tehty.join(" · ") }));
      await paivita();
      // Tallennuksen jälkeen osio sulkeutuu otsikoksi; ilmoitus jää rivin otsikkoon (24.9.2026).
      if (auki) avaa();
    } catch (e) {
      const syy = e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.tallennus_virhe");
      setIlmoitus(tehty.length ? t("kay.tallennettu_osin", { osat: tehty.join(" · "), syy }) : t("kay.mitaan_ei_tallennettu", { syy }));
      if (tehty.length) await paivita();
    } finally { setTallennetaan(false); }
  }

  async function vaihdaTila() {
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      if (rivi.laji === "toimittaja" && rivi.aktiivinen && !window.confirm(
        t("kay.passivoi_toimittaja", { nimi: rivi.nimi }))) return;
      await api.muokkaaKayttajaa(rivi.id, { aktiivinen: !rivi.aktiivinen });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.muutos_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function poistaKayttaja() {
    if (!window.confirm(t("kay.poisto_vahvistus", { nimi: rivi.nimi }))) return;

    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.poistaKayttaja(rivi.id);
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.poisto_virhe"));
      setTallennetaan(false);
    }
  }

  async function kutsu() {
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      const t = await api.luoKutsu(rivi.id);
      nayta(rivi.nimi, t.kutsu, "kutsu", { kayttajaId: rivi.id, osoite: t.osoite, syy: t.lahetys.syy });
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.linkki_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function peruKutsu() {
    const peruste = window.prompt(t("kay.peru_kutsu_kysymys", { nimi: rivi.nimi }));
    if (peruste === null) return;
    if (!peruste.trim()) { setIlmoitus(t("kay.peruste_puuttuu")); return; }
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.peruKutsu(rivi.id, peruste.trim());
      setIlmoitus(t("kay.kutsu_mitatoity"));
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.peruutus_virhe"));
    } finally { setTallennetaan(false); }
  }

  async function nollaa() {
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      const t = await api.nollaaSalasana(rivi.id);
      nayta(rivi.nimi, t.salasana, "salasana");
      await paivita();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.nollaus_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <>
      <tr className={(auki ? "tr--auki" : "") + (rivi.aktiivinen ? "" : " tr--passiivi")} data-kayttaja-rivi={rivi.id}>
        <th className="th--nimi">
          <input type="checkbox" checked={valittu} onChange={valitse} aria-label={t("oik.joukko_valitse")}
                 data-joukko-valinta={rivi.id} style={{ width: "auto", marginRight: 6 }} />
          {rivi.nimi}
          {/* T530 (d): paketit näkyvät myös listan rivillä. */}
          {(rivi.paketit ?? []).length > 0 && <span className="kokonimi" data-rivi-paketit={(rivi.paketit ?? []).map((p) => p.paketti).join(",")}>
            {(rivi.paketit ?? []).map((p) => t(`oik.paketti.${p.paketti}.nimi` as KaannosAvain)).join(" · ")}</span>}
          {!auki && ilmoitus && <span className="kokonimi" role="status" data-rivin-ilmoitus="">{ilmoitus}</span>}
          {/* Tunnusnumero (Toni 24.9.2026): samalla ihmisellä voi olla selaintunnus ja
              kenttätili, ja tuessa tunnukseen viitataan numerolla — ruudulta sen on
              oltava tarkistettavissa. Toissijainen merkintä, ei oma sarake. */}
          <span className="tunnusnumero" data-tunnus={rivi.id}>#{rivi.id}</span>
          {/* Liitos näytetään AINA, myös kun nimet ovat samat.
              Aiempi ehto piilotti sen juuri tavallisimmassa tapauksessa,
              ja kommentti perusteli sen sillä että "rivillä lukee ei
              liitosta" — sellaista tekstiä ei rivillä ollut.
              Kolme lukijaa piilotti saman liitoksen: tämä ehto, lomakkeen
              pudotusvalikko, ja Istuntoja-sarake joka luettiin
              liitokseksi. */}
          {rivi.henkilo_id === null
            ? <span className="kokonimi dim">{t("kay.ei_henkiloliitosta")}</span>
            : rivi.henkilo !== rivi.nimi
              ? <span className="kokonimi">{rivi.henkilo}</span>
              : <span className="kokonimi dim">{t("kay.henkilo_liitetty_tila")}</span>}
        </th>
        <td className="td--vasen mono">{rivi.sahkoposti ?? <span className="dim" data-osoite="ei">{t("kay.ei_sahkopostia")}</span>}</td>
        <td className="td--vasen">{rivi.roolit.join(" · ") || <span className="dim">—</span>}</td>
        <td className="td--vasen">
          {rivi.kohteet.length === 0
            ? <span className="dim">{t("kay.kaikki_kohteet")}</span>
            : <span className="nakyvyys--rajattu">
                {rivi.kohteet.length === 1 ? t("kay.kohde_1") : t("kay.kohdetta_n", { n: rivi.kohteet.length })}
              </span>}
        </td>
        <td className="mono">
          {rivi.viimeksi_sisaan
            ? new Date(rivi.viimeksi_sisaan).toLocaleDateString("fi-FI")
            : <span className="dim">{t("kay.ei_kertaakaan")}</span>}
        </td>
        <td className="mono">
          {rivi.istuntoja
            ? <span title={t("kay.istunnot_title")}>{rivi.istuntoja}</span>
            : <span className="dim">—</span>}
        </td>
        <td className="mono">
          {rivi.aktiivinen ? t("kay.aktiivinen") : t("kay.passivoitu")}
          {/* B2: kutsun tila näkyy siinä rivissä johon se kuuluu — tila, ei arvon puute. */}
          {rivi.kutsu_tila === "avoin" && (
            <>
              <span className="dim" title={t("kay.kutsu_odottaa", { pvm: naytaPvm(rivi.kutsu_vanhenee) ?? "" })}> · {t("kay.kutsu_avoin")}</span>
              {/* 122: avoin kutsu on peruttavissa — avain jota ei voi ottaa pois ei ole hallinnassa. */}
              <button type="button" className="nappi nappi--kevyt" style={{ marginLeft: 6, fontSize: 11, padding: "1px 6px" }}
                      disabled={tallennetaan} onClick={() => void peruKutsu()}>{t("kay.mitatoi_kutsu")}</button>
            </>
          )}
          {rivi.kutsu_tila === "peruttu" && !rivi.viimeksi_sisaan && <span className="dim"> · {t("kay.kutsu_peruttu")}</span>}
          {rivi.kutsu_tila === "vanhentunut" && !rivi.viimeksi_sisaan && (
            <>
              <Tilasana laji="vika" title={t("kay.kutsu_vanheni_title", { pvm: naytaPvm(rivi.kutsu_vanhenee) ?? "" })}> · {t("kay.kutsu_vanheni")}</Tilasana>
              {/* Toni 11.9.: tila on nimetty oikein, mutta korjaus kuuluu samalle riville. */}
              {rivi.laji !== "jarjestelma" && (
                <button type="button" className="nappi nappi--kevyt" style={{ marginLeft: 6, fontSize: 11, padding: "1px 6px" }}
                        disabled={tallennetaan} onClick={() => void kutsu()} data-uusi-kutsu="">{t("kay.uusi_kutsu")}</button>
              )}
            </>
          )}
        </td>
        <td className="td--oikea">
          <button className="nappi nappi--kevyt" onClick={avaa} aria-expanded={auki}>
            {auki ? t("hl.sulje") : t("qr.muokkaa")}
          </button>
        </td>
      </tr>

      {auki && (
        <tr className="tr--laajennus">
          <td colSpan={8} className="td--vasen">
            <div className="laajennus" ref={laajennusRef}>
              {/* Rivi 83: osoite on reitti jota pitkin kutsu kulkee — vaihdettavissa
                  ilman poistoa. Kenttätilit eivät ole tässä listassa (125). */}
              {/* T530 tiivistys (Tonin katselmointi 12.10.2026): tunnus ja osoite yhdellä rivillä, kenttä vasta «Vaihda»-napista. */}
              <div className="tun__rivi" data-tunnus-otsikko="">
                <span>{t("kay.tunnus_lyhyt")} <strong className="mono" data-tunnusnumero={rivi.id}>{rivi.id}</strong></span>
                <button type="button" className="nappi nappi--kevyt nappi--pieni"
                        onClick={() => { void navigator.clipboard?.writeText(String(rivi.id)).then(
                          () => setIlmoitus(t("kay.tunnusnumero_kopioitu", { n: rivi.id })), () => setIlmoitus(t("kay.kopiointi_virhe"))); }}>
                  {t("sal.kopioi")}
                </button>
                <OsoiteMuokkaus rivi={rivi} paivita={paivita} />
              </div>
              <h3 className="tun__ryhmaotsikko">{t("kay.roolit")}</h3>
              <div className="osaamiset">
                {roolit.map((r) => {
                  // T685 (Toni 29.10.2026 klo 14.45 kohta 3): Tarkastaja vain ulkopuoliselle. Sisäiseltä tunnukselta sen voi vain poistaa,
                  // jos se on jo annettu (poisto Tonin rivillä); uutta ei voi valita. Sama ehto palvelimella (tarkastaja_vain_ulkopuoliselle).
                  const vainUlko = r.tunnus === "tarkastaja" && rivi.laji !== "ulkopuolinen" && !rivi.roolit.includes("tarkastaja");
                  return (
                  <label key={r.id} data-rooli={r.tunnus} data-rooli-este={vainUlko ? "vain_ulkopuoliselle" : undefined}
                         className={"valinta" + (valitut.includes(r.tunnus) ? " valinta--on" : "")}
                         title={vainUlko ? t("kay.tarkastaja_vain_ulkopuoliselle") : r.kuvaus ?? undefined}>
                    <input type="checkbox" checked={valitut.includes(r.tunnus)} disabled={vainUlko}
                           onChange={() => setValitut((v) =>
                             v.includes(r.tunnus) ? v.filter((x) => x !== r.tunnus) : [...v, r.tunnus])} />
                    {r.nimi}
                  </label>
                  );
                })}
              </div>

              <div className="tun__rivi" data-henkilo-rivi="">
                <span className="tun__nimi">{t("tk.henkilo")}:</span>
                {/* value + ID, ei defaultValue + nimi (valikko näytti «ei liitosta» myös liitetylle). 106: tunnusta ei voi
                    irrottaa ihmisestä, vain siirtää — tyhjää vaihtoehtoa ei ole. */}
                {rivi.laji === "henkilo" ? (
                  <select value={henkiloValinta} disabled={tallennetaan} aria-label={t("tk.henkilo")} className="tun__valinta"
                          onChange={(e) => { setHenkiloValinta(e.target.value); void liitaHenkilo(e.target.value); }}>
                    {henkilot.map((h) => (
                      <option key={h.id} value={String(h.id)}>{h.nimi}</option>
                    ))}
                  </select>
                ) : (
                  <span className="dim">
                    {rivi.laji === "jarjestelma" ? t("kay.laji.jarjestelma")
                      : rivi.laji === "toimittaja" ? t("kay.laji.toimittaja") : t("kay.laji.ulkopuolinen")}
                  </span>
                )}
                <InfoNappi nimi={t("tk.henkilo")}><span>{t("kay.henkilo_vihje")}</span></InfoNappi>
              </div>

              <div className="tun__rivi" data-arkaluonteiset-rivi="">
                <label className={"valinta" + (arkaluonteiset ? " valinta--on" : "")}>
                  <input type="checkbox" checked={arkaluonteiset} disabled={tallennetaan}
                         onChange={() => void vaihdaArkaluonteiset()} />
                  {t("kay.arkaluonteiset_valinta")}
                </label>
                {/* Selitys ⓘ:n takana; «Tallentuu heti» korvattu tallennuksen jälkeisellä kuittauksella. Katselun loki ennallaan. */}
                <InfoNappi nimi={t("kay.arkaluonteiset")}><span>{t("kay.arkaluonteiset_vihje")}</span></InfoNappi>
                {arkKuittaus && <span className="dim" role="status" data-arkaluonteiset-kuittaus="">{arkKuittaus}</span>}
              </div>

              {/* 67 B (5.10.2026): sama editori kenttätileillä — yksi komponentti, ei kahta kopiota. */}
              <OikeusEditori kayttajaId={rivi.id} oikeusluettelo={oikeusluettelo} kohteet={kohteet} tunnukset={tunnukset}
                             paivita={paivita} ilmoita={setIlmoitus} />

              <h3>{t("kay.nakyvyys")}</h3>
              {/* T685 kohta 1: sama ehto kuin palvelimen naekeeKaiken (auth.ts) — tarkastaja ja ulkopuolinen eivät näe ilman valintaa mitään. */}
              <p className="ikkuna__vihje" style={{ margin: "0 0 8px" }}
                 data-nakyvyys-vihje={rivi.laji === "ulkopuolinen" || valitut.includes("tarkastaja") ? "ei_mitaan" : "kaikki"}>
                {rivi.laji === "ulkopuolinen" || valitut.includes("tarkastaja") ? t("kay.nakyvyys_vihje_rajattu") : t("kay.nakyvyys_vihje")}
              </p>
              <div className="osaamiset">
                {kohteet.map((ko) => (
                  <label key={ko.id} data-nakyvyys={ko.id}
                         className={"valinta" + (valitutKohteet.includes(ko.id) ? " valinta--on" : "")}>
                    <input type="checkbox" checked={valitutKohteet.includes(ko.id)}
                           onChange={() => setValitutKohteet((v) =>
                             v.includes(ko.id) ? v.filter((x) => x !== ko.id) : [...v, ko.id])} />
                    {ko.tunnus} · {ko.nimi}
                  </label>
                ))}
                {kohteet.length === 0 && <span className="dim">{t("kay.ei_kohteita")}</span>}
              </div>

              {/* 117: laji rajaa elinkaaren — napit joita laji kieltää eivät ole ruudulla.
                  Vahti: ei roolimuutosta, ei kutsua, ei salasanaa, ei passivointia, ei poistoa.
                  Toimittaja: ei poistoa; passivointi sallittu. API kieltäytyy samoista. */}
              <div className="allokointi">
                {rivi.laji !== "jarjestelma" && (
                  /* Harmaa kun tallennettavaa ei ole — viereinen teksti kertoo miksi (24.9.2026).
                     Ennen painallus tyhjällä tulosti saman lauseen toiseen kertaan alemmas. */
                  <button className="nappi nappi--paa" disabled={tallennetaan || tallentamatta.length === 0}
                          onClick={() => void tallenna()} data-tallenna-muutokset="">
                    {tallennetaan ? t("kay.tallennetaan") : t("kay.tallenna_muutokset")}
                  </button>
                )}
                {rivi.laji !== "jarjestelma" && (
                  <span className={tallentamatta.length ? "" : "dim"} data-tallentamatta={tallentamatta.join(",")}>
                    {tallentamatta.length ? t("kay.tallentamatta", { osat: tallentamatta.map((x) => x === "roolit" ? t("kay.osa.roolit") : t("kay.osa.nakyvyys")).join(t("kay.ja")) }) : t("kay.ei_tallentamattomia_tila")}
                  </span>
                )}
                {rivi.laji !== "jarjestelma" && (
                  <>
                    <button className="nappi nappi--kevyt" disabled={tallennetaan}
                            onClick={() => void kutsu()}>
                      {t("kay.luo_kutsulinkki")}
                    </button>
                    <button className="nappi nappi--kevyt" disabled={tallennetaan}
                            onClick={() => void nollaa()}>
                      {t("kay.kertasalasana")}
                    </button>
                  </>
                )}
                {!oma && rivi.laji !== "jarjestelma" && (
                  <button className="nappi nappi--kevyt" disabled={tallennetaan}
                          onClick={() => void vaihdaTila()}>
                    {rivi.aktiivinen ? t("kay.passivoi") : t("kay.palauta")}
                  </button>
                )}
                {!oma && rivi.laji !== "jarjestelma" && rivi.laji !== "toimittaja" && (
                  <button className="nappi nappi--poisto" disabled={tallennetaan}
                          onClick={() => void poistaKayttaja()}>
                    {t("kay.poista_lopullisesti")}
                  </button>
                )}
                {rivi.laji === "jarjestelma" && <span className="dim">{t("kay.jarjestelma_ei_muokata")}</span>}
              </div>

              <p className="ikkuna__vihje">
                {oma
                  ? t("kay.oma_tunnus")
                  : t("kay.passivointi_vihje")}
                {" "}{t("kay.kutsulinkki_vihje")}
              </p>

              {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── uusi käyttäjä ──────────────────────────────────────── */

/*
 * 106: yleinen "Uusi käyttäjä" -lomake poistui. Ihmisen tunnukset luodaan
 * henkilökortilta (Henkilöt-näkymä), koska identiteetti on henkilössä ja
 * pääsy on tunnuksessa — tili ilman ihmistä ei ole enää mahdollinen tila.
 * Tämä lomake luo VAIN ulkopuolisen tunnuksen (tilaaja, auditoija):
 * ei ihminen talon rekisterissä, aina lukija, laji tulee reitistä.
 */
/* ── Uusi tunnus: haku ensin, laji johdettuna, seuraus näkyviin ennen tallennusta ──
   Toni 11.9.2026. Ennen tätä pääsysivun ainoa ovi oli «Ulkopuolinen tunnus» ja
   tavallinen tapaus (oman väen tunnus) tehtiin henkilökortilta — poikkeus oli
   sivun päätoiminto, ja sana «ulkopuolinen» luki väitteenä ihmisestä. Mittaus:
   molemmat napit olivat sama toimenpide (luoTunnus) eri kiinteillä parametreilla;
   laji rajaa rooleja (107) ja elinkaarta (117), EI näkyvyyttä (kayttaja_kohde).

   Siksi: 1) kysytään ensin KUKA — haku henkilöstöön; demossa 10 ja pekilossa 41
   ihmistä on ilman mitään tunnusta, joten tavallinen tapaus on että ihminen on jo
   rekisterissä. 2) Laji on JOHDETTU: löytyi → henkilo, ei löytynyt → ulkopuolinen.
   Kukaan ei lue eikä kirjoita sanaa. 3) Johdos ja sen seuraus sanotaan ENNEN
   tallennusta — johdettu arvo joka muuttaa oikeuksia eikä näy on sama vika kuin
   kaikki muutkin tällä viikolla. 4) Kohderajaus on erillinen valinta, koska se
   ei ole koskaan ollutkaan lajiin kytketty.

   SÄÄNTÖ (päätös, ei sivutuote): vuokra- ja alihankintatyöntekijä on henkilöstössä,
   joten hänen tunnuksensa on talon tunnus (laji henkilo) — hän hitsaa ja kirjaa, ja
   kirjaus hänen omissa nimissään on jäljitettävyyden ehto. Ulkopuolinen on vain
   se, jota henkilöstössä ei ole (tilaaja, auditoija). */
function UusiTunnus({
  henkilot, roolit, kohteet, rivit, peruuta, valmis,
}: {
  henkilot: Resurssi[]; roolit: Rooli[]; kohteet: Kohde[]; rivit: KayttajaRivi[];
  peruuta: () => void;
  valmis: (nimi: string, kutsu: string, lisa: { kayttajaId: number; osoite: string; syy: string | null }) => Promise<void>;
}) {
  const [haku, setHaku] = useState("");
  const [valittu, setValittu] = useState<Resurssi | null>(null);
  const [eiHenkilostossa, setEiHenkilostossa] = useState(false);
  const [sahkoposti, setSahkoposti] = useState("");
  const [valitutRoolit, setValitutRoolit] = useState<string[]>(["lukija"]);
  /* Kannan liipaisin on portti (107 + 135); tämä lista on sen näkymä, ja ne
     on pidettävä samana. Väärä valinta täällä ei vuoda mitään — se kaatuu
     tallennuksessa check_violationiin — mutta lomake joka tarjoaa mahdottoman
     on sama vika kuin valikkorivi joka vastaa 403:lla (T345). */
  const ULKOPUOLISEN_ROOLIT = ["lukija", "tarkastaja"];
  const [valitutKohteet, setValitutKohteet] = useState<number[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);

  const hakusana = haku.trim().toLowerCase();
  const osumat = hakusana.length >= 2 && !valittu
    ? henkilot.filter((h) => !h.nimeamaton && h.nimi.toLowerCase().includes(hakusana)).slice(0, 8)
    : [];
  const onJoTunnus = (h: Resurssi) => rivit.some((r) => r.henkilo_id === h.id && !r.kenttatunnus);
  // Johdos: löytyi henkilöstöstä → henkilo; ei löytynyt (ihminen vahvisti) → ulkopuolinen.
  const laji: "henkilo" | "ulkopuolinen" | null = valittu ? "henkilo" : eiHenkilostossa ? "ulkopuolinen" : null;
  const nimi = valittu ? valittu.nimi : haku.trim();

  async function tallenna(e: FormEvent) {
    e.preventDefault();
    if (!laji) return;
    setVirhe(null); setTallennetaan(true);
    try {
      const tl = laji === "henkilo"
        ? await api.luoKayttajatili(valittu!.id, { sahkoposti: sahkoposti.trim(), roolit: valitutRoolit })
        : await api.luoUlkopuolinen({ sahkoposti: sahkoposti.trim(), nimi, kohde_idt: valitutKohteet });
      if (laji === "henkilo" && valitutKohteet.length) await api.asetaKayttajanKohteet(tl.id, valitutKohteet);
      await valmis(nimi, tl.kutsu, { kayttajaId: tl.id, osoite: sahkoposti.trim(),
        syy: laji === "ulkopuolinen" ? t("kay.ulkopuolinen_ei_kutsua") : null });
    } catch (err) {
      setVirhe(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("kay.tallennus_virhe"));
      setTallennetaan(false);
    }
  }

  return (
    <section className="panel lomake" data-uusi-tunnus-lomake="">
      <div className="panel__head">
        <h2>{t("kay.uusi_tunnus")}</h2>
        <span className="hint">
          {t("kay.uusi_hint")}
        </span>
      </div>
      <form className="lomake__kentat" onSubmit={(e) => void tallenna(e)}>
        <label className="kasvaa">
          {t("kay.kuka")}
          <input value={haku} autoFocus autoComplete="off" placeholder={t("kay.hae_placeholder")}
                 onChange={(e) => { setHaku(e.target.value); setValittu(null); setEiHenkilostossa(false); }} />
        </label>
        {osumat.length > 0 && (
          <div className="osaamiset" data-osumat={osumat.length}>
            {osumat.map((h) => (
              <button key={h.id} type="button" className="valinta" disabled={onJoTunnus(h)}
                      title={onJoTunnus(h) ? t("kay.on_jo_tunnus_title") : undefined}
                      onClick={() => { setValittu(h); setEiHenkilostossa(false); }}>
                {h.nimi}{h.lyhenne ? ` · ${h.lyhenne}` : ""}{onJoTunnus(h) ? ` — ${t("kay.tunnus_on_jo")}` : ""}
              </button>
            ))}
          </div>
        )}
        {hakusana.length >= 2 && !valittu && !eiHenkilostossa && (
          <p className="hint">
            {osumat.length === 0 ? t("kay.ei_osumia") : t("kay.valitse_osuma")}{" "}
            <button type="button" className="nappi nappi--kevyt" onClick={() => setEiHenkilostossa(true)}>
              {t("kay.luo_ulkopuolisena")}
            </button>
          </p>
        )}

        {/* Johdos ja sen seuraus ENNEN tallennusta. */}
        {laji === "henkilo" && valittu && (
          <p className="huom" role="status" data-johdos="henkilo">
            {t("kay.johdos.loytyi")} <strong>{valittu.nimi}</strong>
            {valittu.tyosuhde && valittu.tyosuhde !== "oma" ? ` (${valittu.tyosuhde})` : ""}.
            {" "}{t("kay.johdos.luodaan")} <strong>{t("kay.johdos.talon_tunnus")}</strong>{t("kay.johdos.talon_loppu")}
            {valittu.tyosuhde && valittu.tyosuhde !== "oma" && (
              <> {t("kay.johdos.vuokra")}</>
            )}
          </p>
        )}
        {laji === "ulkopuolinen" && (
          <p className="huom" role="status" data-johdos="ulkopuolinen">
            {t("kay.johdos.ei_henkilostossa")} <strong>{t("kay.johdos.ulkopuolisena")}</strong>{t("kay.johdos.ulkopuolinen_loppu")}
          </p>
        )}

        {laji && (
          <>
            <label className="kasvaa">
              {t("kirjautuminen.sahkoposti")}
              <input type="email" value={sahkoposti} autoComplete="off" required
                     onChange={(e) => setSahkoposti(e.target.value)} />
            </label>
            <div className="lomake__osaamiset">
              <span className="lomake__otsikko">{t("kay.roolit")}</span>
              <div className="osaamiset">
                {roolit.map((r) => (
                  /* 135 (T344): ulkopuolinen saa olla lukija TAI tarkastaja — tarkastuslaitos on
                     ulkopuolinen, ja ilman tätä roolia ei voisi antaa kenellekään jolle se on
                     tarkoitettu. Muut roolit ovat yhä kiellettyjä, ja kannan liipaisin sanoo sen
                     samoin sanoin: tämä valinta ei ole se portti, vain sen näkymä. */
                  /* T685 (Toni 29.10.2026 klo 14.45 kohta 3): Tarkastaja ei ole annettavissa sisäiselle tunnukselle. */
                  <label key={r.id} className={"valinta" + (valitutRoolit.includes(r.tunnus) ? " valinta--on" : "")}
                         data-rooli-este={laji !== "ulkopuolinen" && r.tunnus === "tarkastaja" ? "vain_ulkopuoliselle" : undefined}
                         title={laji === "ulkopuolinen" && !ULKOPUOLISEN_ROOLIT.includes(r.tunnus)
                                  ? t("kay.ulkopuolinen_roolit")
                                  : laji !== "ulkopuolinen" && r.tunnus === "tarkastaja" ? t("kay.tarkastaja_vain_ulkopuoliselle")
                                  : r.kuvaus ?? undefined}>
                    <input type="checkbox"
                           checked={laji === "ulkopuolinen"
                             ? (ULKOPUOLISEN_ROOLIT.includes(r.tunnus) && valitutRoolit.includes(r.tunnus))
                             : r.tunnus !== "tarkastaja" && valitutRoolit.includes(r.tunnus)}
                           disabled={(laji === "ulkopuolinen" && !ULKOPUOLISEN_ROOLIT.includes(r.tunnus)) || (laji !== "ulkopuolinen" && r.tunnus === "tarkastaja")}
                           onChange={() => setValitutRoolit((v) => v.includes(r.tunnus) ? v.filter((x) => x !== r.tunnus) : [...v, r.tunnus])} />
                    {r.nimi}
                  </label>
                ))}
              </div>
            </div>
            <div className="lomake__osaamiset">
              <span className="lomake__otsikko">{t("kay.nakyvyys")}</span>
              <div className="osaamiset">
                {kohteet.map((ko) => (
                  <label key={ko.id} className={"valinta" + (valitutKohteet.includes(ko.id) ? " valinta--on" : "")}>
                    <input type="checkbox" checked={valitutKohteet.includes(ko.id)}
                           onChange={() => setValitutKohteet((v) => v.includes(ko.id) ? v.filter((x) => x !== ko.id) : [...v, ko.id])} />
                    {ko.tunnus}
                  </label>
                ))}
                {/* T644: ulkopuolinen ilman kohdetta ei näe mitään, joten kohde on pakollinen eikä «ei rajausta». */}
                <span className="dim" data-ulkopuolinen-kohde={laji === "ulkopuolinen" && valitutKohteet.length === 0 ? "puuttuu" : undefined}>
                  {valitutKohteet.length > 0 ? t("kay.rajattu_n", { n: valitutKohteet.length })
                    : laji === "ulkopuolinen" ? t("kay.ulkopuolinen_kohde_pakollinen") : t("kay.ei_rajausta")}
                </span>
              </div>
            </div>
          </>
        )}

        <div className="lomake__toiminnot">
          <button type="submit" className="nappi nappi--paa" disabled={tallennetaan || !laji || (laji === "henkilo" && valitutRoolit.length === 0)
                    || (laji === "ulkopuolinen" && valitutKohteet.length === 0)}>
            {tallennetaan ? t("kay.luodaan") : laji === "ulkopuolinen" ? t("kay.luo_ulkopuolinen") : t("kay.luo_tunnus_kutsu")}
          </button>
          <button type="button" className="nappi nappi--kevyt" onClick={peruuta}>{t("hl.peruuta")}</button>
        </div>
        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      </form>
    </section>
  );
}

function Moduulit() {
  const [moduulit, setModuulit] = useState<Moduuli[]>([]);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  const lataa = useCallback(async () => {
    try { setModuulit(await api.moduulit()); } catch { setModuulit([]); }
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  async function vaihda(m: Moduuli) {
    setTallennetaan(true);
    setIlmoitus(null);
    try {
      await api.vaihdaModuuli(m.tunnus, !m.kaytossa);
      setIlmoitus(
        m.kaytossa
          ? t("kay.moduuli_pois", { nimi: m.nimi })
          : t("kay.moduuli_paalle", { nimi: m.nimi })
      );
      await lataa();
    } catch (e) {
      setIlmoitus(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.muutos_virhe"));
    } finally { setTallennetaan(false); }
  }

  if (moduulit.length === 0) return null;

  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{t("kay.moduulit")}</h2>
        <span className="hint">{t("kay.moduulit_hint")}</span>
      </div>
      <ul className="moduulilista">
        {moduulit.map((m) => (
          <li key={m.tunnus} className={m.kaytossa ? "" : "moduuli--pois"}>
            <div>
              <div className="moduuli__nimi">
                {kantaTeksti("moduuli", m.tunnus, "nimi", m.nimi)}
                {m.pakollinen && <span className="moduuli__ydin">{t("kay.ydin")}</span>}
              </div>
              {m.kuvaus && <div className="moduuli__kuvaus">{kantaTeksti("moduuli", m.tunnus, "kuvaus", m.kuvaus)}</div>}
            </div>
            <button
              className={"nappi " + (m.kaytossa ? "nappi--kevyt" : "nappi--paa")}
              disabled={tallennetaan || m.pakollinen}
              title={m.pakollinen ? t("kay.ydin_title") : undefined}
              onClick={() => void vaihda(m)}
            >
              {m.kaytossa ? t("kay.kaytossa") : t("kay.pois_kaytosta")}
            </button>
          </li>
        ))}
      </ul>
      {ilmoitus && <p className="huom" role="status">{ilmoitus}</p>}

      <p className="ikkuna__vihje" style={{ padding: "0 12px 12px" }}>
        {t("kay.moduulit_vihje")}
      </p>
    </section>
  );
}

/* ── Kenttätilien joukkoluonti ────────────────────────────────
   Ehdokkaat = henkilöt joilla on koodi (lyhenne) mutta ei tiliä.
   Luonti antaa kullekin PIN:n (näytetään KERRAN). Kirjautuminen
   kenttäkoodi + PIN kirjautumissivun "Kenttätili"-tilassa.

   Nimi on tarkoituksella ammattiton: sama tili palvelee hitsaajaa,
   sähköasentajaa, putkiasentajaa ja käyttöönottajaa. "Kenttä" kertoo
   missä ja miten — puhelimella, omalla koodilla — eikä mitä ammattia. */
/* 67 B (Toni 5.10.2026): erilliset oikeudet yhtenä komponenttina selaintunnuksen ja kenttätilin rivillä. T530 (12.10.2026)
   korvasi oikeus × kohde -ruudukon `OikeusEditori`lla (paketit, Lisäasetukset, yksi kohdevalinta). */

function KenttatilitLuonti({ onValmis, kohteet, oikeusluettelo }: { onValmis: () => Promise<void>; kohteet: Kohde[];
  oikeusluettelo: { tunnus: string; nimi: string; kuvaus: string | null; kohderajattava: boolean; moduuli?: string | null; moduuli_kaytossa?: boolean }[] }) {
  const [ehdokkaita, setEhdokkaita] = useState<number | null>(null);
  // T288 (13.9.2026): napautus valitsee. Tyhjä valinta ei luo mitään — ennen tätä nappi loi tilin kaikille.
  const [ehdokkaat, setEhdokkaat] = useState<{ id: number; koodi: string; nimi: string }[]>([]);
  const [valitut, setValitut] = useState<number[]>([]);
  const [haku, setHaku] = useState("");
  const [tilit, setTilit] = useState<Kenttatili[] | null>(null);
  const [tehdaan, setTehdaan] = useState(false);
  const [tulos, setTulos] = useState<{ koodi: string; nimi: string; pin: string }[] | null>(null);
  const [uusiPin, setUusiPin] = useState<{ koodi: string; nimi: string; pin: string } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  // Päätös 13.9.2026: luonti pakottaa työmaan — tyhjä valinta ei ole «kaikki».
  const [tyomaat, setTyomaat] = useState<number[]>([]);
  // Norjan erä 8.10.2026: kieli asetetaan luonnissa, ja olemassa oleville usealle kerralla.
  const [luontiKieli, setLuontiKieli] = useState("");
  const [kieliValitut, setKieliValitut] = useState<number[]>([]);
  const [joukkoKieli, setJoukkoKieli] = useState("");
  const [naytaPassiiviset, setNaytaPassiiviset] = useState(false);
  const naytettavat = (tilit ?? []).filter((x) => x.aktiivinen || naytaPassiiviset);
  const [kieliIlmoitus, setKieliIlmoitus] = useState<string | null>(null);
  const [muokattava, setMuokattava] = useState<{ kayttaja_id: number; idt: number[] } | null>(null);
  // 67 B: minkä kenttätilin oikeuseditori on auki (kayttaja_id).
  const [oikeudetAuki, setOikeudetAuki] = useState<number | null>(null);
  const [oikeusIlmoitus, setOikeusIlmoitus] = useState<string | null>(null);

  const tunnukset = (idt: number[]) =>
    idt.map((id) => kohteet.find((x) => x.id === id)?.tunnus ?? `#${id}`).join(", ");

  const lataa = useCallback(async () => {
    const [e, t] = await Promise.all([
      api.kenttatilitEhdokkaat().catch(() => []),
      api.kenttatilit().catch(() => []),
    ]);
    setEhdokkaita(e.length);
    setEhdokkaat(e);
    setValitut((v) => v.filter((id) => e.some((x) => x.id === id)));
    setTilit(t);
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  async function luo() {
    // Työmaa valinnainen (Toni 13.9.2026): ilman sitä tili näkee omat tietonsa mutta ei yhtään kohdetta.
    const kohde = tyomaat.length === 0
      ? t("kay.kt.ilman_tyomaata")
      : t("kay.kt.tyomaalle", { tunnukset: tunnukset(tyomaat) });
    if (valitut.length === 0) return;
    if (!window.confirm(t("kay.kt.luo_vahvistus", { n: valitut.length, kohde }))) return;
    setTehdaan(true); setVirhe(null);
    try {
      const r = await api.luoKenttatilit(valitut, tyomaat, luontiKieli || null);
      setTulos(r.tilit);
      if (r.ei_ehdokkaita > 0) setVirhe(t("kay.kt.ei_saanut", { n: r.ei_ehdokkaita }));
      setTyomaat([]);
      setValitut([]);
      setHaku("");
      setUusiPin(null);
      await lataa();
      await onValmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.luonti_virhe"));
    } finally { setTehdaan(false); }
  }

  /* Kieli usealle kenttätilille kerralla (Norjan erä 8.10.2026: «koko Norjan porukka englanniksi»). Työntekijä voi yhä
   * vaihtaa omansa Omista tiedoista; tämä asettaa sen jolla sovellus avautuu. Ei katkaise istuntoja: kieli luetaan
   * seuraavalla sivun latauksella. */
  async function asetaKieliValituille() {
    if (kieliValitut.length === 0) return;
    setTehdaan(true); setVirhe(null); setKieliIlmoitus(null);
    try {
      const r = await api.asetaTunnustenKieli(kieliValitut, joukkoKieli || null);
      setKieliIlmoitus(t("kay.kt.kieli_asetettu", { kieli: joukkoKieli ? KIELEN_NIMI[joukkoKieli as Kieli] : t("kay.kt.org_oletus"), m: r.muutettu, v: r.valittu })
        + (r.muutettu < r.valittu ? t("kay.kt.oli_jo", { n: r.valittu - r.muutettu }) : "."));
      setKieliValitut([]);
      await lataa();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.kieli_virhe"));
    } finally { setTehdaan(false); }
  }

  /* Uusi PIN olemassa olevalle tilille.
   *
   * Reitti `POST /api/kenttatilit/:id/pin` oli olemassa 28.8.2026 asti
   * ILMAN YHTÄÄN KUTSUJAA, ja paneeli katosi kokonaan kun kaikki tilit
   * oli luotu (`ehdokkaita === 0` → `return null`). Unohtuneelle PIN:lle
   * ei siis ollut mitään tehtävissä käyttöliittymästä.
   *
   * Vahvistus kysytään, koska tämä KATKAISEE istunnot: kentällä oleva
   * kirjautuminen päättyy siihen hetkeen. */
  async function annaUusiPin(kt: Kenttatili) {
    if (!window.confirm(t("kay.uusi_pin_vahvistus", { nimi: kt.nimi, koodi: kt.koodi }))) return;
    setTehdaan(true); setVirhe(null);
    try {
      setUusiPin(await api.uusiPin(kt.id));
      setTulos(null);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.pin_virhe"));
    } finally { setTehdaan(false); }
  }

  /* Työmaiden muutos olemassa olevalle tilille. Sama reitti kuin selaintunnuksen
   * rajaus (PUT /api/kayttajat/:id/kohteet), ja se katkaisee tilin istunnot.
   * Tyhjä on sallittu (Toni 13.9.2026): sitomaton kenttätili ei näe kohteita, näkee omat tietonsa. */
  async function tallennaTyomaat() {
    if (!muokattava) return;
    setTehdaan(true); setVirhe(null);
    try {
      await api.asetaKayttajanKohteet(muokattava.kayttaja_id, muokattava.idt);
      setMuokattava(null);
      await lataa();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("kay.tyomaat_virhe"));
    } finally { setTehdaan(false); }
  }

  /** Työmaan tunnus ja mistä näkyvyys tulee. Ei-aktiivinen näkyy merkittynä, koska kentällä se ei näy. */
  // Toni 13.9.2026 («näyttää kömpelöltä»): solu kantoi neljää asiaa — tunnukset, lähteen, selityksen ja painikkeen.
  // Näkyvyys on TYÖMAAN ominaisuus, ei rivin (Cowork 13.9.): samalla tilillä voi olla näkyvä N-2405 ja suunnitteilla
  // oleva N-2401, joten rivikohtainen tila valehtelisi toisesta. Siksi jokainen työmaa on oma sirunsa ja tila on sirussa —
  // piilotettu himmeänä JA sanalla «ei näy» (ei pelkän värin varassa). Rivin tila vain kun yksikään työmaa ei näy.
  // Sama ehto kuin kannan f_kohde_varaa peilin kautta — ei omaa kopiota säännöstä (13.9.2026).
  const nakyyKentalla = (id: number) => { const ko = kohteet.find((x) => x.id === id); return !!ko && varaaKapasiteettia(ko.tila); };
  function tyomaaMerkki(id: number, lahde: "allokaatiosta" | "lisäsidonta") {
    const ko = kohteet.find((x) => x.id === id);
    const nakyy = nakyyKentalla(id);
    return (
      <span key={`${lahde}-${id}`} className={nakyy ? undefined : "dim"} data-lahde={lahde} data-nakyy={nakyy ? "kylla" : "ei"}
            style={{ display: "inline-block", border: "1px solid var(--viiva)", padding: "1px 6px", margin: "0 6px 4px 0", whiteSpace: "nowrap" }}
            title={ko ? `${ko.nimi} · ${lahde === "lisäsidonta" ? t("kay.kt.lahde_sidonta") : t("kay.kt.lahde_allokaatio")} · ${t("kay.kt.tyomaa_tila", { tila: nayttonimiPieni("kohde_tila", ko.tila) })}${nakyy ? "" : ` — ${t("kay.kt.ei_nay_kentalla")}`}` : (lahde === "lisäsidonta" ? t("kay.kt.lahde_sidonta") : t("kay.kt.lahde_allokaatio"))}>
        <span className="mono">{ko?.tunnus ?? `#${id}`}</span>
        {lahde === "lisäsidonta" && <span style={{ fontSize: 13 }}> · {t("kay.kt.sidonta")}</span>}
        {!nakyy && <span style={{ fontSize: 13 }}> · {t("kay.kt.ei_nay", { tila: ko?.tila ?? t("kay.kt.tuntematon") })}</span>}
      </span>
    );
  }

  if (ehdokkaita === null || tilit === null) return null;
  if (ehdokkaita === 0 && tilit.length === 0 && !tulos) return null;

  return (
    <section className="panel scroll">
      <div className="panel__head">
        <h2>{t("kay.lista.kenttatilit")}</h2>
        {ehdokkaita > 0 && !tulos && (
          <button className="nappi nappi--paa" disabled={tehdaan || valitut.length === 0} onClick={luo}>
            {tehdaan ? t("kay.luodaan")
              : valitut.length === 0 ? t("kay.kt.valitse_henkilot")
              : tyomaat.length === 0 ? t("kay.kt.luo_ilman", { n: valitut.length }) : t("kay.kt.luo_n", { n: valitut.length })}
          </button>
          )}
          {ehdokkaita > 0 && !tulos && !tehdaan && valitut.length === 0 && (
            <span className="este-syy" data-este-syy="">{t("kay.kt.syy_valitse")}</span>
        )}
      </div>

      {ehdokkaita > 0 && !tulos && (
        <>
          <p className="dim">
            {t("kay.kt.ohje", { n: ehdokkaita })}
          </p>
          <div style={{ padding: "0 12px 8px", display: "grid", gap: 8 }} data-kenttatili-ehdokkaat="">
            <input value={haku} placeholder={t("kay.kt.hae")} autoComplete="off"
                   onChange={(e) => setHaku(e.target.value)} style={{ maxWidth: 320 }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", maxHeight: 220, overflowY: "auto" }}>
              {ehdokkaat
                .filter((h) => !haku.trim()
                  || `${h.nimi} ${h.koodi}`.toLowerCase().includes(haku.trim().toLowerCase()))
                .map((h) => (
                  <label key={h.id} style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                    <input type="checkbox" checked={valitut.includes(h.id)}
                           onChange={(e) => setValitut((v) => e.target.checked ? [...v, h.id] : v.filter((x) => x !== h.id))} />
                    <span className="mono">{h.koodi}</span>
                    <span>{h.nimi}</span>
                  </label>
                ))}
            </div>
            <p className="hint" style={{ margin: 0 }}>
              {t("kay.kt.valittuna_x_y", { x: valitut.length, y: ehdokkaita })}{haku.trim() ? ` — ${t("kay.kt.haku_rajaa")}` : ""}.
            </p>
            <TyomaaValinta kohteet={kohteet} valitut={tyomaat} muuta={setTyomaat} />
            <TunnuksenKieli arvo={luontiKieli} muuta={setLuontiKieli} merkinta="luonti" />
          </div>
        </>
      )}
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

      {/* A8: PIN:t modaalissa, joka pakottaa kuittauksen ja tarjoaa kopioinnin ja tulostuksen. */}
      {tulos && (
        <Salaisuus otsikko={t("kay.kt.pinit_otsikko")} kohde={t("kay.kt.luotu_n", { n: tulos.length })}
          rivit={tulos.map((t) => ({ selite: `${t.nimi} · ${t.koodi}`, arvo: t.pin }))}
          vihje={t("kay.kt.pinit_vihje")}
          sulje={() => setTulos(null)} />
      )}
      {uusiPin && (
        <Salaisuus otsikko={t("kay.kt.uusi_pin")} kohde={`${uusiPin.nimi} · ${uusiPin.koodi}`}
          rivit={[{ selite: t("kirjautuminen.pin"), arvo: uusiPin.pin }]}
          vihje={t("kay.kt.uusi_pin_vihje")}
          sulje={() => setUusiPin(null)} />
      )}

      {tilit.length > 0 && (
        <>
          <h3 style={{ fontSize: 11, letterSpacing: ".13em", textTransform: "uppercase",
                       margin: "18px 12px 6px", color: "var(--himmea)" }}>
            {t("kay.kt.olemassa")}
          </h3>
          {/* Toni 11.9.: sarake jonka arvo on rakenteellisesti sama kaikilla ei ole sarake —
              se on yksi lause taulukon yläpuolella. Ja otsikko lupaa «mihin»: sarake Työmaa
              tulee allokaatiosta (rivi 91), «ei allokaatiota» on tila. */}
          <p className="hint" style={{ margin: "0 12px 6px" }}>
            {t("kay.kt.ei_sahkopostia")}
            {tilit.filter((x) => x.sahkoposti_keksitty).length > 0 && (
              <> {t("kay.kt.keksitty", { n: tilit.filter((x) => x.sahkoposti_keksitty).length })}</>
            )}
          </p>
          <p className="hint" style={{ margin: "0 12px 6px" }}>
            {t("kay.kt.tyomaat_selite")}
          </p>
          <div style={{ margin: "0 12px 6px" }}>
            <NaytaPassiiviset arvo={naytaPassiiviset} muuta={setNaytaPassiiviset}
                              n={tilit.filter((x) => !x.aktiivinen).length} merkinta="kenttatilit" />
          </div>
          <div className="allokointi" style={{ alignItems: "center", gap: 10, flexWrap: "wrap", margin: "0 12px 8px" }}
               data-kieli-joukko="">
            <span className="dim">{t("kay.kt.valittuna", { n: kieliValitut.length })}</span>
            <TunnuksenKieli arvo={joukkoKieli} muuta={setJoukkoKieli} merkinta="joukko" />
            <button className="nappi nappi--kevyt" disabled={tehdaan || kieliValitut.length === 0} data-kieli-aseta=""
                    onClick={() => void asetaKieliValituille()}>{t("kay.kt.aseta_kieli")}</button>
            {kieliIlmoitus && <span data-kieli-ilmoitus="">{kieliIlmoitus}</span>}
          </div>
          <table className="tbl">
            <thead>
              <tr><th>
                    <input type="checkbox" aria-label={t("kay.kt.valitse_kaikki_aria")} data-kieli-kaikki=""
                           checked={naytettavat.length > 0 && naytettavat.every((x) => kieliValitut.includes(x.kayttaja_id))}
                           onChange={(e) => setKieliValitut(e.target.checked ? naytettavat.map((x) => x.kayttaja_id) : [])} />
                  </th>
                  <th className="th--vasen">{t("kay.kt.koodi")}</th><th className="th--vasen">{t("iv.nimi")}</th>
                  <th className="th--vasen">{t("kieli.otsikko")}</th>
                  <th className="th--vasen">{t("kay.kt.tyomaat")}</th>
                  <th className="th--vasen">{t("kay.kt.viimeksi")}</th><th /></tr>
            </thead>
            <tbody>
              {naytettavat.map((tl) => (<Fragment key={tl.id}>
                <tr>
                  <td>
                    <input type="checkbox" aria-label={t("kay.kt.valitse_aria", { koodi: tl.koodi })} data-kieli-valinta={tl.kayttaja_id}
                           checked={kieliValitut.includes(tl.kayttaja_id)}
                           onChange={(e) => setKieliValitut((v) => e.target.checked ? [...v, tl.kayttaja_id] : v.filter((x) => x !== tl.kayttaja_id))} />
                  </td>
                  <td className="mono">{tl.koodi}</td>
                  <td>
                    {tl.nimi}
                    <span className="tunnusnumero" data-tunnus={tl.kayttaja_id}>#{tl.kayttaja_id}</span>
                    {/* 67 B: oikeudet samalla editorilla kuin selaintunnuksilla; lukumäärä näkyy napissa. */}
                    <button className="nappi nappi--kevyt" data-kenttatili-oikeudet={tl.kayttaja_id} style={{ marginLeft: 8 }}
                            onClick={() => { setOikeusIlmoitus(null); setOikeudetAuki(oikeudetAuki === tl.kayttaja_id ? null : tl.kayttaja_id); }}>
                      {t("kay.kt.oikeudet")}{(tl.oikeudet?.length ?? 0) + (tl.kohdeoikeudet?.length ?? 0) > 0 ? ` (${(tl.oikeudet?.length ?? 0) + (tl.kohdeoikeudet?.length ?? 0)})` : ""}
                    </button>
                    {/* PÄÄTTYNYT TYÖSUHDE JA ELÄVÄ KENTTÄTILI OVAT ERI
                        ASIA, ja vain jälkimmäinen näkyi tässä. Mitattu
                        2.9.2026: kolmella päättyneellä oli aktiivinen
                        tili ja PIN asetettuna — lähtenyt hitsaaja voi
                        kirjautua ja kirjata hitsejä.

                        Tiliä EI suljeta automaattisesti: väärä lukitus
                        on oma vahinkonsa (tauko, palaava
                        aliurakoitsija), ja se on tuotepäätös.
                        Pääkäyttäjä näkee merkinnän ja päättää. */}
                    {tl.paatti && (
                      <span style={{ marginLeft: 8, padding: "1px 6px", fontSize: 11,
                                     fontWeight: 800, letterSpacing: "0.04em",
                                     color: "var(--puna)", border: "2px solid var(--puna)",
                                     whiteSpace: "nowrap" }}
                            title={t("kay.kt.paattynyt_title")}>
                        {t("kay.kt.paattynyt")} {new Date(tl.paatti).toLocaleDateString("fi-FI")}
                      </span>
                    )}
                    {/* 51: merkintä ei ole hallintakeino — tämä on: kirjautuminen kieltäytyy. */}
                    {tl.kirjautuminen_estetty && (
                      <span className="dim" data-kirjautuminen="estetty" style={{ marginLeft: 6 }}>
                        · {t("kay.kt.estetty")}
                      </span>
                    )}
                    {/* 168: PIN on pääkäyttäjän asettama, kunnes omistaja vaihtaa sen itse. */}
                    {tl.pin_vaihdettava && (
                      <span className="dim" data-pin-tila="vaihdettava" style={{ marginLeft: 6 }}
                            title={t("kay.kt.pin_vaihdettava_title")}>
                        · {t("kay.kt.pin_vaihdettava")}
                      </span>
                    )}
                  </td>
                  <td data-tili-kieli={tl.kieli ?? ""}>{tl.kieli ? KIELEN_NIMI[tl.kieli as Kieli] ?? tl.kieli : <span className="dim">{t("kay.kt.oletus")}</span>}</td>
                  {/* Päätökset 13.9.2026: sidonta seuraa allokaatiosta, käsin tehty sidonta on poikkeus.
                      Näkyvyys = molempien yhdiste; muu kuin aktiivinen työmaa ei näy kentällä. */}
                  <td className="td--vasen" data-tyomaat={tl.kohde_idt.join(",") || "ei"}
                      data-johdetut={tl.johdetut_kohde_idt.join(",") || "ei"}>
                    {muokattava?.kayttaja_id === tl.kayttaja_id ? (
                      <div style={{ display: "grid", gap: 6 }}>
                        <TyomaaValinta kohteet={kohteet} valitut={muokattava.idt}
                          muuta={(idt) => setMuokattava({ kayttaja_id: tl.kayttaja_id, idt })} />
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <button className="nappi nappi--paa" disabled={tehdaan}
                                  onClick={() => void tallennaTyomaat()}>{t("ltar.tallenna")}</button>
                          <button className="nappi nappi--kevyt" onClick={() => setMuokattava(null)}>{t("hl.peruuta")}</button>
                          <span className="hint">{t("kay.kt.lisasidonta_hint")}</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {tl.johdetut_kohde_idt.map((id) => tyomaaMerkki(id, "allokaatiosta"))}
                        {tl.kohde_idt.filter((id) => !tl.johdetut_kohde_idt.includes(id))
                          .map((id) => tyomaaMerkki(id, "lisäsidonta"))}
                        {![...tl.johdetut_kohde_idt, ...tl.kohde_idt].some(nakyyKentalla) && (
                          <Tilasana laji="varoitus">{t("kay.kt.ei_nakyvyytta")}</Tilasana>
                        )}
                      </>
                    )}
                  </td>
                  <td>{tl.viimeksi_sisaan
                    ? <span className="mono">{naytaPvm(tl.viimeksi_sisaan)}</span>
                    : <span className="dim">{t("kay.ei_kertaakaan")}</span>}</td>
                  <td className="td--oikea" style={{ whiteSpace: "nowrap" }}>
                    {muokattava?.kayttaja_id !== tl.kayttaja_id && (
                      <button type="button" disabled={tehdaan}
                              style={{ background: "none", border: 0, padding: 0, font: "inherit", color: "inherit",
                                       textDecoration: "underline", cursor: "pointer", marginRight: 14 }}
                              onClick={() => setMuokattava({ kayttaja_id: tl.kayttaja_id, idt: tl.kohde_idt })}>
                        {t("kay.kt.lisasidonnat")}
                      </button>
                    )}
                    <button className="nappi nappi--kevyt" disabled={tehdaan}
                            onClick={() => void annaUusiPin(tl)}>{t("kay.kt.uusi_pin")}</button>
                  </td>
                </tr>
                {oikeudetAuki === tl.kayttaja_id && (
                  <tr data-kenttatili-oikeudet-auki={tl.kayttaja_id}>
                    <td colSpan={5} style={{ padding: "6px 12px 10px" }}>
                      {oikeusIlmoitus && <p className="hint" role="status">{oikeusIlmoitus}</p>}
                      <OikeusEditori kayttajaId={tl.kayttaja_id} oikeusluettelo={oikeusluettelo} kohteet={kohteet}
                                     tunnukset={tilit.map((x) => ({ id: x.kayttaja_id, nimi: x.nimi }))} paivita={lataa} ilmoita={setOikeusIlmoitus} />
                    </td>
                  </tr>
                )}
              </Fragment>))}
            </tbody>
          </table>
          <p className="hint" style={{ padding: "8px 12px 12px" }}>
            {t("kay.kt.unohtunut_pin")}
          </p>
        </>
      )}
    </section>
  );
}

/* ── Rivi 83: sähköpostiosoitteen vaihto selaintunnukselle ─────────────
   Osoite on reitti jota pitkin salaisuus kulkee (B2). Ennen tätä väärin
   kirjoitettu osoite korjattiin poistamalla tunnus ja luomalla uusi — juuri
   se poisto jota muualla on kolmesti kielletty. Poiston esto (117) seuraa
   lajia, ei osoitetta, joten vaihto ei anna eikä vie poikkeusta. */
function OsoiteMuokkaus({ rivi, paivita }: { rivi: { id: number; sahkoposti: string | null; laji?: string | null }; paivita: () => Promise<void> }) {
  const [osoite, setOsoite] = useState(rivi.sahkoposti ?? "");
  const [tila, setTila] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [muokataan, setMuokataan] = useState(false);
  useEffect(() => { setOsoite(rivi.sahkoposti ?? ""); setTila(null); setMuokataan(false); }, [rivi.id, rivi.sahkoposti]);
  if (rivi.laji === "jarjestelma") return null;
  const muuttunut = osoite.trim().toLowerCase() !== (rivi.sahkoposti ?? "");
  async function tallenna(e: FormEvent) {
    e.preventDefault();
    setTallennetaan(true); setTila(null);
    try {
      await api.muokkaaKayttajaa(rivi.id, { sahkoposti: osoite.trim() });
      setTila(t("kay.osoite_vaihdettu"));
      await paivita();
    } catch (err) {
      setTila(err instanceof ApiError ? virheTeksti(err.virhe, err.message) : t("pin.vaihto_virhe"));
    } finally { setTallennetaan(false); }
  }
  if (!muokataan) return (
    <span className="tun__osoite" data-osoitemuokkaus={rivi.id}>
      <span className="tun__erotin">·</span>
      {rivi.sahkoposti ? <span className="mono">{rivi.sahkoposti}</span> : <span className="dim">{t("kay.ei_sahkopostia")}</span>}
      <button type="button" className="nappi nappi--kevyt nappi--pieni" onClick={() => setMuokataan(true)} data-osoite-vaihda="">{t("kay.vaihda")}</button>
      {tila && <span className="dim" role="status">{tila}</span>}
    </span>
  );
  return (
    <form className="tun__osoite" onSubmit={(e) => void tallenna(e)} data-osoitemuokkaus={rivi.id}>
      <span className="tun__erotin">·</span>
      <input type="email" value={osoite} autoComplete="off" required disabled={tallennetaan} aria-label={t("kirjautuminen.sahkoposti")}
             onChange={(e) => setOsoite(e.target.value)} className="tun__osoitekentta" autoFocus />
      <button type="submit" className="nappi nappi--kevyt nappi--pieni" disabled={tallennetaan || !muuttunut}>
        {tallennetaan ? t("pin.vaihdetaan") : t("kay.vaihda_osoite")}
      </button>
      <button type="button" className="nappi nappi--kevyt nappi--pieni" onClick={() => { setOsoite(rivi.sahkoposti ?? ""); setMuokataan(false); }}>{t("oik.peru")}</button>
      {tila && <span className="dim" role="status">{tila}</span>}
    </form>
  );
}

/** «Näytä passiiviset» (12.10.2026): oletus pois, ja piilotettujen määrä sanotaan — piilotettu rivi ei saa näyttää olemattomalta. */
function NaytaPassiiviset({ arvo, muuta, n, merkinta }: {
  arvo: boolean; muuta: (v: boolean) => void; n: number; merkinta: string;
}) {
  if (n === 0) return null;
  return (
    <label className="hint" style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: "0 0 8px" }}
           data-nayta-passiiviset={merkinta}>
      <input type="checkbox" checked={arvo} onChange={(e) => muuta(e.target.checked)} />
      {arvo ? t("kay.passiiviset.naytetaan", { n }) : t("kay.passiiviset.piilossa", { n })}
    </label>
  );
}
